import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import { updateStudent } from '../../services/adminService';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import toast from 'react-hot-toast';

// Та же модалка, что ученик открывает у себя в профиле, но для админа:
// только имя и фамилия. Аватар показываем, менять его админ не может —
// это личное дело ученика; цели по баллам здесь тоже нет.
const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 11,
  border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-1)',
  fontSize: 14, fontFamily: 'inherit', outline: 'none',
};

const EditStudentNameModal = ({ isOpen, onClose, student, onSaved }) => {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [trial, setTrial] = useState(false);
  const [saving, setSaving] = useState(false);

  // При каждом открытии подставляем актуальное имя: в базе оно одной строкой —
  // первое слово имя, остальное фамилия
  useEffect(() => {
    if (!isOpen) return;
    const parts = (student?.name || '').trim().split(/\s+/).filter(Boolean);
    setFirst(parts[0] || '');
    setLast(parts.slice(1).join(' '));
    setTrial(!!student?.isTrial);
  }, [isOpen, student]);

  const handleSave = async () => {
    const trimmed = [first.trim(), last.trim()].filter(Boolean).join(' ');
    if (!first.trim()) { toast.error('Введите имя'); return; }
    const trialChanged = trial !== !!student?.isTrial;
    if (trimmed === (student?.name || '') && !trialChanged) { onClose(); return; }
    setSaving(true);
    try {
      await updateStudent(student.id, { name: trimmed, isTrial: trial });
      toast.success('Профиль обновлён');
      onSaved?.(trimmed);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') handleSave(); };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Редактировать профиль" size="small">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', color: '#fff',
          background: student?.avatar ? 'var(--primary)' : avatarColor(student?.id),
          display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--border)' }}>
          {student?.avatar
            ? <img src={student.avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <AvatarIcon id={student?.id} size={40} />}
        </div>

        {/* Имя и фамилия — двумя полями в одну строку; если места мало, переносятся */}
        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>
              Имя
            </label>
            <input type="text" value={first} autoFocus maxLength={50} placeholder="Иван" style={FIELD}
              onChange={e => setFirst(e.target.value)} onKeyDown={onKey} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6 }}>
              Фамилия
            </label>
            <input type="text" value={last} maxLength={50} placeholder="Иванов" style={FIELD}
              onChange={e => setLast(e.target.value)} onKeyDown={onKey} />
          </div>
        </div>

        {/* Пометка «Пробный» — та же, что ставится при добавлении ученика */}
        <label style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px',
          borderRadius: 10, border: '1px solid var(--border)',
          background: trial ? 'var(--primary-light)' : 'var(--bg)', cursor: 'pointer' }}>
          <input type="checkbox" checked={trial} onChange={e => setTrial(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0 }} />
          <span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>Пробный</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              {trial
                ? 'Дата доступа уберётся, вместо неё останется пометка. В «Доступах» такой ученик встаёт первым, а пометка снимется сама при продлении.'
                : 'Пометка снимется, но дата доступа не появится сама — проставьте её продлением в «Доступах».'}
            </span>
          </span>
        </label>

        {/* Кнопки */}
        <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 4 }}>
          <button type="button" onClick={onClose} disabled={saving}
            style={{ flex: 1, padding: '11px', borderRadius: 11, border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text-2)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
            Отмена
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '11px', borderRadius: 11, border: 'none',
              background: 'var(--primary)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditStudentNameModal;
